import React, { useMemo, useState } from 'react';
import { PageContainer } from 'src/features/Signup/components/organisms/PageContainer/index';
import { AccountDetailStep } from 'src/features/Signup/components/templates/AccountCreationPage/AccountDetailsStep';
// import CreditCardModal from 'src/features/Signup/components/organisms/CreditCardModal';
import { signUp } from 'aws-amplify/auth';
import { FormProvider, useForm } from 'react-hook-form';
import { ErrorNotifBox } from 'src/components/atoms/ErrorNotifBox';
import { useAuthContext } from 'src/contexts/AuthContext';
import { AnalyzingSpinner } from 'src/features/DataUpload/components/organisms/AnalyzingSpinner/index';
import { Box } from '@chakra-ui/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { decodeJwt } from 'jose';
import { CustomerRole } from 'src/contexts/AuthContext';
import { denormalizeCompanyGroupName } from 'src/contexts/AuthContext/utils';
import { DEFAULT_CURRENCY_CODE, getCurrencyLabel } from 'src/utils/currency';

// Two mutually-exclusive flows:
//   'create' — invite issued by Yapamar staff for a brand-new company. The
//              token carries role='admin' and NO customerId. The invitee
//              fills the full company-onboarding form.
//   'join'   — invite issued by an existing customer admin to add someone
//              (as user OR admin) to their tenant. The token carries a
//              customerId; role determines the invitee's permissions inside
//              that tenant. The form is minimal (join existing company).
//
// The important distinction is `customerId` presence, NOT role. A customer
// admin promoting someone to admin sends role='admin' + customerId=X — that
// is a 'join' flow, not a 'create' flow.
type TokenInspection =
  | { status: 'none' }
  | { status: 'expired' }
  | { status: 'malformed' }
  | {
      status: 'valid';
      mode: 'create';
      role: CustomerRole.ADMIN;
      email: string;
    }
  | {
      status: 'valid';
      mode: 'join';
      role: CustomerRole;
      email: string;
      customerId: string;
    };

// decodeJwt only base64-decodes — it does NOT verify the signature.
// The trust boundary is the backend re-validating the token in clientMetadata
// during signUp; the client uses this purely to drive UI state.
function inspectToken(token: string | null): TokenInspection {
  if (!token) return { status: 'none' };
  try {
    const payload = decodeJwt(token) as {
      email?: string;
      role?: CustomerRole;
      customerId?: string;
      exp?: number;
    };
    if (
      typeof payload.email !== 'string' ||
      (payload.role !== CustomerRole.ADMIN &&
        payload.role !== CustomerRole.USER)
    ) {
      return { status: 'malformed' };
    }
    // A user-role token without a customerId is a backend bug (invite not
    // bound to a company). Surface it as malformed rather than silently
    // rendering the wrong (admin) form.
    if (
      payload.role === CustomerRole.USER &&
      typeof payload.customerId !== 'string'
    ) {
      return { status: 'malformed' };
    }
    if (typeof payload.exp === 'number' && payload.exp * 1000 < Date.now()) {
      return { status: 'expired' };
    }
    // 'create' vs 'join' is decided by customerId, not role. Customer admins
    // can invite as either user OR admin — both stay inside the existing
    // tenant, so both are 'join'.
    if (typeof payload.customerId === 'string') {
      return {
        status: 'valid',
        mode: 'join',
        role: payload.role,
        email: payload.email,
        customerId: payload.customerId,
      };
    }
    return {
      status: 'valid',
      mode: 'create',
      role: CustomerRole.ADMIN,
      email: payload.email,
    };
  } catch {
    return { status: 'malformed' };
  }
}

type AccountDetailFormData = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  companyName: string;
  companyAddress: string;
  country: {
    value: string;
    label: string;
  };
  currency: {
    value: string;
    label: string;
  };
  city: string;
  customerCategory: string;
  yearRoundUsage: string;
  seasonalUsage: string;
  projectInfo: string;
  brandKeyword: string;
  competitorKeywords: string;
};

export const AccountCreationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('token');
  const inspection = useMemo(() => inspectToken(inviteToken), [inviteToken]);

  if (inspection.status === 'expired') {
    return (
      <Box pt={{ base: '70px', md: '70px' }} px={{ base: 4, md: 24, lg: 32 }}>
        <ErrorNotifBox
          title="Invitation link expired"
          description="This invitation link has expired. Please request a new one from your Yapamar contact."
        />
      </Box>
    );
  }

  if (inspection.status === 'none') {
    return (
      <Box pt={{ base: '70px', md: '70px' }} px={{ base: 4, md: 24, lg: 32 }}>
        <ErrorNotifBox
          title="Signup is invitation-only"
          description="Yapamar accounts are created by invitation. Please reach out to your Yapamar contact to receive an invitation link."
        />
      </Box>
    );
  }

  if (inspection.status === 'malformed') {
    return (
      <Box pt={{ base: '70px', md: '70px' }} px={{ base: 4, md: 24, lg: 32 }}>
        <ErrorNotifBox
          title="Invalid invitation link"
          description="This invitation link is invalid. Please request a new one from your Yapamar contact."
        />
      </Box>
    );
  }

  // `mode` decides which form structure to render.
  // - 'create' → full company-onboarding form (staff invite for a new tenant)
  // - 'join'   → minimal form (existing tenant, role from token controls
  //              post-signup permissions but does NOT change the form UI)
  const isJoining = inspection.mode === 'join';
  const prefillEmail = inspection.email;
  const prefillCompanyName =
    inspection.mode === 'join'
      ? denormalizeCompanyGroupName(inspection.customerId)
      : undefined;

  return (
    <AccountCreationForm
      isJoining={isJoining}
      inviteToken={inviteToken!}
      prefillEmail={prefillEmail}
      prefillCompanyName={prefillCompanyName}
    />
  );
};

type AccountCreationFormProps = {
  isJoining: boolean;
  inviteToken: string;
  prefillEmail: string;
  prefillCompanyName?: string;
};

const AccountCreationForm: React.FC<AccountCreationFormProps> = ({
  isJoining,
  inviteToken,
  prefillEmail,
  prefillCompanyName,
}) => {
  const [isFormValid, setIsFormValid] = useState(false);
  const { signIn } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  // const [isCreditCardModalOpen, setIsCreditCardModalOpen] = useState(false);
  // RHF reads defaultValues once on mount and ignores subsequent changes.
  // Prefills come from a URL token decoded at parent mount and never change.
  const methods = useForm<AccountDetailFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: prefillEmail ?? '',
      password: '',
      confirmPassword: '',
      companyName: prefillCompanyName ?? '',
      companyAddress: '',
      country: { value: '', label: '' },
      currency: {
        value: DEFAULT_CURRENCY_CODE,
        label: getCurrencyLabel(DEFAULT_CURRENCY_CODE),
      },
      city: '',
      // Business Category selector is hidden — default everyone to "general".
      customerCategory: 'general',
      yearRoundUsage: 'yes',
      seasonalUsage: 'yes',
      projectInfo: '',
      brandKeyword: '',
      competitorKeywords: '',
    },
    mode: 'onChange',
  });

  const handleNext = async () => {
    const values = methods.getValues();

    const clientMetadata: Record<string, string> = isJoining
      ? { token: inviteToken }
      : {
          token: inviteToken,
          companyName: String(values.companyName ?? ''),
          country: String(values.country?.label ?? ''),
          currency: String(values.currency?.value || DEFAULT_CURRENCY_CODE),
          city: String(values.city ?? ''),
          companyAddress: String(values.companyAddress ?? ''),
          customerCategory: String(values.customerCategory ?? ''),
          yearRoundUsage: String(values.yearRoundUsage ?? ''),
          seasonalUsage: String(values.seasonalUsage ?? ''),
          projectInfo: String(values.projectInfo ?? ''),
          brandKeyword: String(values.brandKeyword ?? ''),
          competitorKeywords: String(values.competitorKeywords ?? ''),
        };

    try {
      const signUpPromise = signUp({
        username: values.email,
        password: values.password,
        options: {
          userAttributes: {
            email: values.email,
            given_name: values.firstName,
            family_name: values.lastName,
          },
          clientMetadata,
          autoSignIn: { enabled: true },
        },
      });
      setIsLoading(true);
      await signUpPromise;

      // Immediately sign the user in using the newly created credentials
      await signIn(values.email, values.password);
      if (isJoining) {
        navigate('/mmm/overview');
      }
    } catch (error: unknown) {
      const normalizedError = {
        type:
          error && typeof error === 'object' && 'name' in error
            ? ((error as { name?: string }).name ?? 'UnknownError')
            : 'UnknownError',
        message:
          error && typeof error === 'object' && 'message' in error
            ? ((error as { message?: string }).message ??
              'Something went wrong')
            : 'Something went wrong',
      };
      if (normalizedError.type === 'InvalidPasswordException') {
        methods.setError('password', {
          type: 'manual',
          message: normalizedError.message,
        });
      } else if (normalizedError.type === 'UserLambdaValidationException') {
        const stripped = normalizedError.message
          .replace(/^(PreSignUp|PostConfirmation) failed with error /i, '')
          .trim();
        setErrorMessage(
          stripped ||
            'Signup could not be completed. Please try again or contact support.',
        );
      } else {
        setErrorMessage(normalizedError.message);
      }
    } finally {
      setIsLoading(false);
    }

    // setIsCreditCardModalOpen(true);
  };

  if (errorMessage) {
    return (
      <Box pt={{ base: '70px', md: '70px' }} px={{ base: 4, md: 24, lg: 32 }}>
        <ErrorNotifBox
          title="Error creating account"
          description={errorMessage}
        />
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        w="100%"
        minH="100vh"
        bg="surface.panel"
        display="flex"
        justifyContent="center"
        alignItems="center">
        <AnalyzingSpinner
          message="Creating an account..."
          boxProps={{
            border: 'none',
            borderRadius: 'none',
            boxShadow: 'none',
            h: 'auto',
          }}
        />
      </Box>
    );
  }

  return (
    <>
      <PageContainer
        title={'Create the account'}
        subtitle={'Takes a couple of minutes.'}
        hideStepper
        onNext={handleNext}
        isFormValid={isFormValid}
        submitLabel="Create account">
        <FormProvider {...methods}>
          <AccountDetailStep
            onValidationChange={setIsFormValid}
            isJoiningExistingCompany={isJoining}
            prefillEmail={prefillEmail}
          />
        </FormProvider>
      </PageContainer>

      {/* <CreditCardModal
        isOpen={isCreditCardModalOpen}
        onClose={handleCreditCardModalClose}
        onSubmit={handleCreditCardSubmit}
      /> */}
    </>
  );
};
